import React, { useEffect, useState } from "react"
import { Outlet, useNavigate, useParams, useLocation } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import DefaultErrorBoundary from "modules/errors/DefaultErrorBoundary"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import CustomPagination from "components/common/CustomPagination"
import { useTranslation } from "react-i18next"
import CrewMembersTable from "./crewMembersTable"
import CrewFilters from "./components/CrewFilters"
import FilterChips from "ui/FilterChips"
import { useCrewFilters } from "./hooks/useCrewFilters"
import { Flex, Spin } from "antd"
import DeactivateDeleteModals from "./crewDetail/components/DeactivateDeleteModals"
import EditCrewMemberModal from "./crewDetail/components/EditCrewMemberModal"
import AssignExperiencesModal from "ui/AssignExperiencesModal"
import { getCrewProfiles, updateCrewProfile } from "store/crew-profiles"
import { getRoles } from "store/roles"
import { routes } from "router/routes"
import { useCrewDeactivateModal } from "./crewDetail/hooks/useCrewDeactivateModal"
import CrewMobileCardList from "./components/CrewMobileCardList"
import { toaster } from "components/common/Toast"

/**
 * Builds trip_assignments_attributes array for saving experience assignments
 * @param {Array<number>} selectedExperienceIds - Complete list of trip IDs that should be assigned
 * @param {Array<Object>} currentAssignments - Current assignment data with trip_assignment_id and trip_id
 * @returns {Array<Object>} Array of trip assignment objects with proper structure
 */
const buildTripAssignmentsAttributes = (selectedExperienceIds, currentAssignments) => {
  const tripAssignmentsAttributes = []

  // Normalize selected IDs to numbers for comparison
  const selectedTripIds = selectedExperienceIds.map(Number)

  // Create a map of current assignments for easy lookup (trip_id -> assignment_id)
  const currentAssignmentsMap = new Map(currentAssignments.map((assignment) => [Number(assignment.trip_id), assignment.trip_assignment_id]))

  // Process all current assignments (either keep or delete)
  currentAssignments.forEach((assignment) => {
    const tripId = Number(assignment.trip_id)
    if (selectedTripIds.includes(tripId)) {
      // Keep: assignment exists in both current and selected
      tripAssignmentsAttributes.push({
        id: assignment.trip_assignment_id,
        trip_id: assignment.trip_id
      })
    } else {
      // Delete: assignment exists in current but not in selected
      tripAssignmentsAttributes.push({
        id: assignment.trip_assignment_id,
        trip_id: assignment.trip_id,
        _destroy: true
      })
    }
  })

  // Handle additions: Find trip IDs that are in selected but not in current
  selectedTripIds.forEach((tripId) => {
    if (!currentAssignmentsMap.has(tripId)) {
      tripAssignmentsAttributes.push({
        trip_id: tripId
      })
    }
  })

  return tripAssignmentsAttributes
}

export default function CrewMembersPage() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams()
  const [isTabletOrMobile, setIsTabletOrMobile] = useState(false)

  useEffect(() => {
    const checkScreenSize = () => {
      setIsTabletOrMobile(window.innerWidth <= 1024)
    }

    checkScreenSize()
    window.addEventListener("resize", checkScreenSize)
    return () => window.removeEventListener("resize", checkScreenSize)
  }, [])

  // Check if we're on the edit route
  const isEditModalOpen = location.pathname.includes("/edit/")
  const editCrewMemberId = params.id // Will be defined when on /crew/edit/:id

  const handleEdit = (record) => {
    navigate(routes.guideCrewMemberEditFromListPath({ id: record.id }))
  }

  const handleEditCancel = () => {
    navigate(routes.guideResourcesCrewPath())
  }
  // Redux state
  const { data: assignedExperiencesData = [], loading: assignedExperiencesLoading } = useSelector(
    (state) => state.crewProfiles.assignedExperiences
  )

  // Deactivate/Delete modal hook
  const {
    deactivateDeleteModalState,
    activateModalVisible,
    selectedCrew,
    openDeactivateModal,
    openDeleteModal,
    openActivateModal,
    closeDeactivateDeleteModal,
    handleConfirmDeactivateDelete,
    handleActivateConfirm,
    deactivateLoading,
    deleteLoading,
    deactivationDetailsLoading,
    deletionDetailsLoading
  } = useCrewDeactivateModal()

  // Assign experiences modal state
  const [assignModalState, setAssignModalState] = useState({ visible: false, crewMember: null })

  // Custom hooks for filters with Redux backend integration
  const {
    search,
    selectedRoles,
    selectedStatuses,
    selectedDate,
    pageSize,
    roleOptions,
    statusOptions,
    expandedSelectedRoles,
    expandedSelectedStatuses,
    crewData,
    metadata,
    loading,
    dragDisabled,
    setSearch,
    setSelectedRoles,
    setSelectedStatuses,
    setSelectedDate,
    setCurrentPage,
    setPageSize,
    handleTableChange,
    handleReorder
  } = useCrewFilters(t)

  // Fetch roles on mount
  useEffect(() => {
    dispatch(getRoles())
  }, [dispatch])

  const handleDeactivate = (record) => {
    openDeactivateModal(record)
  }

  const handleActivate = (record) => {
    openActivateModal(record)
  }

  const handleDelete = (record) => {
    openDeleteModal(record)
  }

  const handleConfirmModal = async (data) => {
    const result = await handleConfirmDeactivateDelete(data)
    if (result?.success) {
      // Refresh the crew list after successful deactivation/deletion
      dispatch(
        getCrewProfiles({
          search,
          roleIds: selectedRoles,
          status: selectedStatuses.length > 0 ? selectedStatuses[0] : undefined,
          available_on: selectedDate,
          page: metadata.current_page,
          per_page: pageSize
        })
      )
    }
  }

  const handleConfirmActivate = async () => {
    const result = await handleActivateConfirm()
    if (result?.success) {
      // Refresh the crew list after successful activation
      dispatch(
        getCrewProfiles({
          search,
          roleIds: selectedRoles,
          status: selectedStatuses.length > 0 ? selectedStatuses[0] : undefined,
          available_on: selectedDate,
          page: metadata.current_page,
          per_page: pageSize
        })
      )
    }
  }

  const handleAssignSave = async (selectedExperienceIds) => {
    const { crewMember } = assignModalState
    const crewMemberId = crewMember?.key || crewMember?.id

    if (!crewMemberId) {
      toaster.error(t("crew.errors.no_crew_member_selected") || "No crew member selected")
      throw new Error("No crew member selected")
    }

    try {
      // Build proper trip_assignments_attributes with id tracking for updates/deletes
      const tripAssignmentsAttributes = buildTripAssignmentsAttributes(selectedExperienceIds, assignedExperiencesData)

      // Create JSON payload with array of objects
      const payload = {
        trip_assignments_attributes: tripAssignmentsAttributes
      }

      await dispatch(updateCrewProfile(crewMemberId, payload))
      dispatch(
        getCrewProfiles({
          search,
          roleIds: selectedRoles,
          status: selectedStatuses.length > 0 ? selectedStatuses[0] : undefined,
          available_on: selectedDate,
          page: metadata.current_page,
          per_page: pageSize
        })
      )
      setAssignModalState({ visible: false, crewMember: null })
    } catch (error) {
      console.error("Failed to assign experiences:", error)
      throw error
    }
  }

  return (
    <div className="vstack flex-fill z-0 mt-l" id="clients">
      <FirstLoading>
        {/* Hide crew list content when edit modal is open on mobile */}
        {!(isEditModalOpen && isTabletOrMobile) && (
          <div className="flex-fill">
            <CrewFilters
              search={search}
              onSearchChange={setSearch}
              selectedRoles={selectedRoles}
              onRoleChange={setSelectedRoles}
              roleOptions={roleOptions}
              selectedStatuses={selectedStatuses}
              onStatusChange={setSelectedStatuses}
              statusOptions={statusOptions}
              selectedDate={selectedDate}
              onDateChange={setSelectedDate}
            />
            <FilterChips selectedValues={expandedSelectedRoles} options={roleOptions} onChange={setSelectedRoles} className="my-m" />
            <FilterChips
              selectedValues={expandedSelectedStatuses}
              options={statusOptions}
              onChange={setSelectedStatuses}
              className="my-m"
            />

            {loading && crewData.length === 0 ? (
              <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
                <Spin size="large" />
              </div>
            ) : (
              <>
                {isTabletOrMobile ? (
                  <CrewMobileCardList
                    data={crewData}
                    handleEdit={handleEdit}
                    handleActivate={handleActivate}
                    handleDeactivate={handleDeactivate}
                    handleDelete={handleDelete}
                    onReorder={handleReorder}
                    dragDisabled={dragDisabled}
                  />
                ) : (
                  <>
                    <CrewMembersTable
                      data={crewData}
                      onChange={handleTableChange}
                      loading={loading}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onDeactivate={handleDeactivate}
                      onActivate={handleActivate}
                      onReorder={handleReorder}
                      dragDisabled={dragDisabled}
                    />
                    <Flex justify="flex-end" className="mb-s mt-l">
                      <CustomPagination
                        metadata={metadata}
                        pageSize={pageSize}
                        total={metadata.total_results || 0}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={(size) => {
                          setPageSize(size)
                          setCurrentPage(1)
                        }}
                        pageSizeOptions={[5, 10, 20, 50]}
                      />
                    </Flex>
                  </>
                )}
              </>
            )}
          </div>
        )}
        <DefaultErrorBoundary>
          <Outlet />
        </DefaultErrorBoundary>
      </FirstLoading>

      {/* Edit Crew Member Modal */}
      {isEditModalOpen && editCrewMemberId && (
        <EditCrewMemberModal
          open={isEditModalOpen}
          onCancel={handleEditCancel}
          onSave={() => {
            dispatch(
              getCrewProfiles({
                search,
                roleIds: selectedRoles,
                status: selectedStatuses.length > 0 ? selectedStatuses[0] : undefined,
                available_on: selectedDate,
                page: metadata.current_page,
                per_page: pageSize
              })
            )
            handleEditCancel()
          }}
          crewMember={{ id: editCrewMemberId }}
          context="crew"
        />
      )}

      {/* Deactivate/Delete/Activate Modals */}
      <DeactivateDeleteModals
        modalState={deactivateDeleteModalState}
        activateModalVisible={activateModalVisible}
        selectedCrew={selectedCrew}
        onClose={closeDeactivateDeleteModal}
        onConfirm={handleConfirmModal}
        onActivateConfirm={handleConfirmActivate}
        deactivateLoading={deactivateLoading}
        deleteLoading={deleteLoading}
        deactivationDetailsLoading={deactivationDetailsLoading}
        deletionDetailsLoading={deletionDetailsLoading}
        t={t}
      />

      {/* Assign Experiences Modal */}
      <AssignExperiencesModal
        open={assignModalState.visible}
        onCancel={() => setAssignModalState({ visible: false, crewMember: null })}
        onSave={handleAssignSave}
        currentExperiences={assignedExperiencesData.map((exp) => ({ id: exp.trip_id, title: exp.trip_title }))}
        emptyText={t("crewMember.no_experience_assigned", { defaultValue: "No experience assigned to this crew member." })}
        loading={assignedExperiencesLoading}
      />
    </div>
  )
}
